import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  StepIconProps,
  StepIcon,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { TTagsSituacao } from "../../../../../types/ITagSituacao";
import { IDetalheTarefas } from "../../../../../types/api/bpm/detalhar-processo";
import moment from "moment";
import LabelSubtitle from "./LabelSubtitle";
import ContentTarefas from "./ContentTarefas";
import LabelContent from "./LabelContent";

export default function StepperTarefas({
  usuarioSolicitante,
  finalizadoPor,
  situacao,
  motivoFinalizado,
  atualizadoEm,
  tarefas = [],
  codigo,
  extras,
}: StepperTarefasProps) {
  const stepAtivo = useMemo(() => {
    return situacao !== "Finalizado" ? tarefas.length - 1 : tarefas.length + 1;
  }, [tarefas, situacao]);

  //funções manipuladoras
  const handleSearchUltimaResposta = useCallback(
    (keysObj: string[]): TSearchUltimaResposta => {
      const arrRespostas = tarefas
        .filter((obj) => obj.resposta !== null && !obj.cancelada_em)
        .map((obj) => obj.resposta);

      const result = keysObj.map((key) => {
        const obj = arrRespostas.reverse().find((obj) => obj[key]);
        if (obj) {
          return { key, value: obj[key] };
        }
        return null;
      });

      return result;
    },
    [tarefas]
  );
  //

  return (
    <>
      <Typography variant="h6" my={2}>
        Tarefas
      </Typography>

      <Stepper activeStep={stepAtivo} orientation="vertical">
        {tarefas.map((tarefa, index) => {
          const existeErro = Boolean(tarefa.erro);
          const estaCancelada = Boolean(tarefa.cancelada_em);

          return (
            <Step key={tarefa.id}>
              <StepLabel
                StepIconComponent={(props) => (
                  <CustomStepIcon
                    {...props}
                    estaCancelada={estaCancelada}
                    existeErro={existeErro}
                    index={index + 1}
                  />
                )}
                sx={{
                  "& .Mui-active": {
                    color: estaCancelada
                      ? "#d32f2f"
                      : existeErro
                      ? "#d32f2f"
                      : "primary",
                  },
                }}
              >
                <Typography
                  component="div"
                  color="black"
                  sx={{ color: estaCancelada ? "error.main" : "primary" }}
                >
                  {tarefa.titulo}
                </Typography>

                <LabelSubtitle {...{ tarefa, existeErro, estaCancelada }} />

                <LabelContent {...{ tarefa, usuarioSolicitante, codigo }} />
              </StepLabel>

              {!tarefa.resolvida_em && !tarefa.cancelada_em && (
                <StepContent>
                  <ContentTarefas
                    {...{
                      tarefa,
                      handleSearchUltimaResposta,
                      extras,
                      usuarioSolicitante,
                    }}
                  />
                </StepContent>
              )}
            </Step>
          );
        })}

        {situacao === "Finalizado" && (
          <Step>
            <StepLabel>
              <Typography component="div">Finalizado</Typography>
              <Typography component="div" variant="caption">
                Resolvido em {moment(atualizadoEm).format("DD/MM/YYYY HH:mm")}
              </Typography>
              {motivoFinalizado && finalizadoPor &&(
                <>
                  <Typography component="div" variant="caption">
                    Finalizado por: {finalizadoPor}
                  </Typography>
                  <Typography component="div" variant="caption">
                    Motivo: {motivoFinalizado}
                  </Typography>
                </>
              )}
            </StepLabel>
          </Step>
        )}
      </Stepper>
    </>
  );
}

function CustomStepIcon({
  active,
  completed,
  estaCancelada,
  existeErro,
  index,
}: StepIconProps & {
  estaCancelada: boolean;
  existeErro: boolean;
  index: number;
}) {
  if (estaCancelada) return <CancelIcon color="error" />;
  else if (existeErro) return <ErrorIcon color="error" />;
  else if (completed) return <CheckCircleIcon color="primary" />;
  else if (active)
    return <StepIcon {...{ active, completed, icon: index.toString() }} />;
  else return <StepIcon {...{ icon: index.toString() }} />;
}

interface StepperTarefasProps {
  codigo: string;
  usuarioSolicitante: string;
  tarefas: IDetalheTarefas[];
  situacao: TTagsSituacao;
  atualizadoEm: string;
  motivoFinalizado: string;
  finalizadoPor: string | null;
  extras: any;
}

export interface StepperChildrenProps {
  tarefa: IDetalheTarefas;
}

export type TSearchUltimaResposta = Array<{
  key: string;
  value: any;
} | null>;
